// Rich "last activity" summary for a customer, derived from their most recent
// transaction — the web equivalent of Android's CustomerSummary.lastActivityMetaInfo
// + lastAmount, which Android computes locally from the last transaction (the customer
// list API does NOT return it).
//
// Kept dependency-free (stable proto enum values inlined, currency formatting left to
// the caller) so it stays unit-testable under the project's jest module resolution.
export interface ILastActivity {
  metaInfo: number
  amount: number // rupees
  time: number // epoch millis
}

// Mirror of transaction.types enums (proto values, stable).
const TYPE_CREDIT = 1
const CATEGORY_DISCOUNT = 1
const STATE_PROCESSING = 0

// The "last activity" time of a raw cached transaction — a port of the CASE expression
// legacy Android aggregates over in BackendCoreDatabase.kt:
//
//   MAX(CASE WHEN isDeleted == 1 THEN deleteTime
//            WHEN createdAt >= Customer.createdAt
//              THEN (CASE WHEN amountUpdatedAt >= createdAt THEN amountUpdatedAt ELSE createdAt END)
//            ELSE Customer.createdAt END)
//
// Two deliberate details, both of which used to be wrong here:
//   * `bill_date` is NOT part of it. The bill date is the date the merchant assigned to the
//     entry (what the ledger groups by); "last activity" is when the entry was recorded.
//     Folding it into a max meant a post-dated entry reported a date Android never would.
//   * A plain `update_time` bump does NOT count. Android only advances on an AMOUNT edit
//     (`amountUpdatedAt`), so a server-side touch that leaves the amount alone must not move
//     the date — that's what made a 29 Jul discount read as "31 Jul" in the customer list.
//
// The one branch not ported is Android's `ELSE Customer.createdAt` fallback for a transaction
// older than the customer record; this helper only sees transactions.
const rawActivityTime = (txn: any): number => {
  if (txn.deleted) {
    return Number(txn.delete_time) || 0
  }
  const created = Number(txn.create_time) || 0
  const updated = Number(txn.update_time) || 0
  return txn.amount_updated && updated >= created ? updated : created
}

// Mirrors Android's Transaction.lastActivityMetaInfo() (Transaction.kt) on a RAW
// cached transaction (snake_case fields as stored in IndexedDB).
const rawMetaInfo = (txn: any): number => {
  const type = Number(txn.type)
  const category = Number(txn.tx_category)
  const state = Number(txn.transaction_state)
  if (txn.deleted) {
    if (type === TYPE_CREDIT) {
      return 0
    }
    if (category === CATEGORY_DISCOUNT) {
      return 6
    }
    return 1
  }
  if (txn.amount_updated) {
    return type === TYPE_CREDIT ? 8 : 9
  }
  if (state === STATE_PROCESSING) {
    return 5
  }
  if (type === TYPE_CREDIT) {
    return 2
  }
  if (category === CATEGORY_DISCOUNT) {
    return 7
  }
  return 3
}

// Picks the customer's latest transaction and summarises it. Returns null when there
// are no cached transactions, so the caller can fall back to its default subtitle.
export const deriveLastActivity = (rawTxns: any[] | undefined): ILastActivity | null => {
  if (!rawTxns || rawTxns.length === 0) {
    return null
  }
  let last: any = null
  let lastTime = -1
  for (const txn of rawTxns) {
    const t = rawActivityTime(txn)
    if (t > lastTime) {
      lastTime = t
      last = txn
    }
  }
  if (!last) {
    return null
  }
  return {
    metaInfo: rawMetaInfo(last),
    amount: Number(last.amount) / 100,
    time: lastTime
  }
}

// Reconciles the customer API's `last_activity` with what the synced transactions actually
// say. Android has no such split — its ledger list and its row subtitles both read one
// SQL-derived lastActivity column — whereas web sorts on the API field but labels rows from
// the cached transactions, so the two can disagree.
//
// They do, in practice. Measured against the prod Android app on the same business:
//   Sachin Yadav     API 22 Jul 13:17  →  transactions say 30 Jul 10:03
//   Aa Ajin          API 05 Jun 04:24  →  transactions say 04 Jul 17:28
//   +91 94506 72711  API 06 Jun 05:57  →  transactions say 11 Jun 21:01
// Every observed disagreement had the API *behind*, never ahead, and taking the newer of the
// two reproduced Android's on-screen order position for position.
//
// max() rather than a straight overwrite, because the cache is not authoritative either: a
// customer whose transactions have not been synced yet has no derived value at all, and must
// keep whatever the API reported.
export const withDerivedActivity = <T extends { id: string; lastActivityAt?: number }>(
  customers: T[] | undefined,
  activityMap?: Record<string, ILastActivity>
): T[] | undefined => {
  if (!customers || !activityMap) {
    return customers
  }
  return customers.map((customer) => {
    const derived = activityMap[customer.id]?.time || 0
    return derived > (Number(customer.lastActivityAt) || 0)
      ? { ...customer, lastActivityAt: derived }
      : customer
  })
}

// Base i18n key per meta code. The caller appends `_absolute`/`_relative` (whether the
// date needs an "on"/connector) and passes {{amount}} + {{date}} — so each language can
// order the sentence itself (e.g. Hindi puts the date connector after the date). Matches
// the shipping (legacy) Android string set (strings_resources.xml). Returns null for the
// "account added"/unknown case so the caller falls back to its default subtitle.
const KEY_BY_META: Record<number, string> = {
  0: 'activity_credit_deleted',
  1: 'activity_payment_deleted',
  2: 'activity_credit_added',
  3: 'activity_payment_added',
  6: 'activity_discount_deleted',
  7: 'activity_discount_offered',
  8: 'activity_credit_edited',
  9: 'activity_payment_edited'
}

export const getLastActivityKey = (metaInfo: number): string | null => {
  if (metaInfo === 5) {
    return 'activity_processing'
  }
  return KEY_BY_META[metaInfo] || null
}
